// @ts-nocheck
import { interactivityRegistration } from "./Charta";
import { DomQuery, HtmlClassSwitch, WithClass, WithTagName } from "./Dom";
import { ControlMode, WebPageComponent, connectClickOutsideListener, removeClickOutsideListener } from "./WebSite.Page.Component";

export class FormBuilder extends WebPageComponent {
  constructor(element) {
    super(element);

    this.components = new Array();
    this.history = new Array();
    this.future = new Array();

    this.determineElements();
    this.initialize();
  }

  add(element) {
    if (this.target.target.container)
      this.target.target.appendChild(element);
    else
      this.target.target.after(element);

    this.setTarget(element);
  }

  closeInspector(component) {
    if (this.inspector.childNodes.length > 1 && this.target.header != undefined)
      this.target.header.parentNode.insertBefore(this.inspector.childNodes[1], this.target.header.nextSibling);
  }

  createAddComponentHandler(component) {
    return (event) => {
      this.add(component.createInstance(this, this.xmlDocument.createElement(component.name)));
      this.editorComponent.writeToElement();
      this.componentList.toggle();
    };
  }

  determineElements() {
    const query = new DomQuery(this.element);

    // Main components
    this.toolbar = query.getChild(WithClass("Toolbar"));
    this.progress = new Progress(query.getChild(WithClass("Progress")));
    this.problems = query.getChild(WithClass("Problems"));
    this.editor = query.getChild(WithClass("Editor"));
    this.inspector = query.getChild(WithClass("Inspector"));
    this.previewElement = query.getChild(WithClass("Preview"));
    this.tools = query.getChild(WithClass("Components"));
    this.valueField = query.getChild(WithTagName("INPUT"));

    this.componentElements = query.getDescendants(WithClass("Component"));

    this.pristine = this.valueField.value;
  }

  getComponent(name, type) {
    for (const component of this.components) {
      if (component.name == name) {
        if (type == undefined)
          return component;
        else if (component.element.dataset.FieldType == type)
          return component;
      }
    }
  }

  initialize() {
    this.initializeToolbar();
    this.initializeComponents();

    this.xmlDocument = document.implementation.createDocument("", "", null);

    this.initializeEditor();
    this.loadForm();

    this.observer = new MutationObserver((mutations) => { this.updateValue(); });
    this.observer.observe(this.editorComponent.object, { attributes: true, childList: true, subtree: true });

    this.editor.scrollTop = 0;
    this.updateToolbar();
  }

  initializeComponents() {
    this.preview = new HtmlClassSwitch(this.element, "Preview");
    this.componentList = new HtmlClassSwitch(this.tools, "Expanded");

    for (const componentElement of this.componentElements) {
      const component = createFormBuilderComponent(componentElement, this.isEditable());

      component.element.onclick = this.createAddComponentHandler(component);
      this.components.push(component);
    }
  }

  initializeEditor() {
    this.editorComponent = new Editor(this.editor, this.xmlDocument.createElement("Form"), "Form");
    this.editor.onclick = (event) => {
      this.setTarget(this.editorComponent);

      if (event != null)
        event.stopPropagation();
    };
  }

  initializeToolbar() {
    const query = new DomQuery(this.toolbar);

    // Toolbar actions
    this.saveButton = query.getChild(WithClass("Save"));
    this.previousButton = query.getChild(WithClass("Previous"));
    this.nextButton = query.getChild(WithClass("Next"));
    this.addButton = query.getChild(WithClass("Add"));
    this.previewButton = query.getChild(WithClass("Preview"));
    this.submitButton = query.getChild(WithClass("Submit"));

    this.previewButton.onclick = (event) => { this.togglePreview(); };
  }

  isEditable() {
    return this.mode === ControlMode.edit;
  }

  loadChildren(element, parent) {
    const query = new DomQuery(parent.element);

    for (const child of element.childNodes) {
      const component = this.getComponent(child.tagName, child.getAttribute("FieldType"));

      if (component != undefined) {
        const instance = component.createInstance(this, child);
        this.add(instance);

        if (instance.container)
          this.loadChildren(child, instance);

        this.setTarget(parent);
      }
      else {
        const value = query.getChild(WithClass(child.tagName));

        if (value != undefined && value.component != undefined && value.component.container)
          this.loadChildren(child, value.component);
      }
    }
  }

  loadForm() {
    const value = this.valueField.value;
    this.setTarget(this.editorComponent);

    if (value.length > 0) {
      const parser = new DOMParser();
      const element = parser.parseFromString(value, "application/xml");

      if (element != undefined) {
        this.editor.component.object = element.childNodes[0];
        this.loadChildren(element.childNodes[0], this.target);
      }
    }
  }

  next() {
    this.history.push(this.valueField.value);
    this.valueField.value = this.future.shift();
    this.reload();
    this.valueChanged();
  }

  openInspector() {
    if (this.target.form != undefined)
      this.inspector.appendChild(this.target.form);
  }

  openPreview() {
    this.previewElement.innerHTML = "";
    this.toggleButton(false, this.saveButton, null);
    this.toggleButton(false, this.previousButton, null);
    this.toggleButton(false, this.nextButton, null);
    this.toggleButton(false, this.addButton, null);
    this.toggleButton(true, this.submitButton, (event) => { this.submit(); });

    this.client.sendJsonRequest(
      this.element.dataset.Uri,
      {
        "Command": "Preview"
      },
      (response) => {
        if (response.status === 200) {
          interactivityRegistration.detach(this.previewElement);
          this.previewElement.innerHTML = response.responseText;
          interactivityRegistration.attach(this.previewElement);
        }
      },
      this.progress
    );
  }

  previous() {
    this.future.unshift(this.valueField.value);
    this.valueField.value = this.history.pop();
    this.reload();
    this.valueChanged();
  }

  reload() {
    this.observer.disconnect();
    this.editorComponent.object = this.xmlDocument.createElement("Form");
    this.editorComponent.clear();
    this.loadForm();
    this.editor.scrollTop = 0;
    this.observer.observe(this.editorComponent.object, { attributes: true, childList: true, subtree: true });
  }

  remove(element) {
    element.remove();
    this.setTarget(element.parent);
  }

  save() {
    if (this.pristine !== this.valueField.value) {
      this.client.sendJsonRequest(
        this.element.dataset.Uri,
        {
          "Command": "Save",
          "Value": this.valueField.value
        },
        (response) => {
          if (response.status === 200) {
            this.setSuccessful();
            this.pristine = this.valueField.value;
            this.valueChanged();
          }
          else if (response.status === 400) {
            this.toolbar.classList.add("Error");
            const listener = connectClickOutsideListener(this.toolbar, (event) => {
              this.toolbar.classList.remove("Error");
              this.problems.innerHTML = "";
              removeClickOutsideListener(listener);
            });

            this.problems.innerHTML = response.responseText;
          }
        },
        this.progress
      );
    }
  }

  setSuccessful() {
    this.toolbar.classList.add("Successful");
    const listener = connectClickOutsideListener(this.toolbar, (event) => { this.toolbar.classList.remove("Successful"); removeClickOutsideListener(listener); });
  }

  setTarget(element) {
    if (element.canFocus()) {
      if (this.target != undefined) {
        this.target.blur();

        this.closeInspector();
      }

      this.target = element;
      this.target.focus();

      this.openInspector();
    }
    else if (element.parent.canFocus())
      this.setTarget(element.parent);
  }

  submit(element) {
    const formData = new FormData(this.previewElement.childNodes[0]);

    formData.append("Action", "Submit");

    this.client.postFormRequest(
      this.element.dataset.Uri,
      formData,
      (xmlRequest) => {
        if (xmlRequest.status === 200) {
          this.setSuccessful();
          this.previewElement.innerHTML = xmlRequest.responseText;
          interactivityRegistration.attach(this.previewElement);
        }
      },
      this.progress
    );
  }

  toggleButton(status, button, function_) {
    if (status) {
      button.onclick = function_;
      button.classList.add("Enabled");
    }
    else {
      button.onclick = null;
      button.classList.remove("Enabled");
    }
  }

  togglePreview() {
    if (!this.preview.getStatus())
      this.openPreview();
    else
      this.updateToolbar();

    this.preview.toggle();
  }

  updateToolbar() {
    this.toggleButton(this.isEditable() && this.valueField.value !== this.pristine, this.saveButton, (event) => { this.save(); });
    this.toggleButton(this.isEditable() && this.history.length > 0, this.previousButton, (event) => { this.previous(); });
    this.toggleButton(this.isEditable() && this.future.length > 0, this.nextButton, (event) => { this.next(); });
    this.toggleButton(this.isEditable(), this.addButton, (event) => { this.componentList.toggle(); });
    this.toggleButton(false, this.submitButton, null);
  }

  updateValue() {
    const serializer = new XMLSerializer();
    const previous = this.valueField.value;

    this.valueField.value = serializer.serializeToString(this.editorComponent.object);

    if (this.valueField.value !== previous) {
      this.history.push(previous);
      this.future = new Array();
      this.valueChanged();
    }
  }

  valueChanged() {
    this.updateToolbar();
  }
}

function createFormBuilderComponent(element, editable) {
  var name = element.dataset.Name;

  if (name === "Section")
    return new FormSectionComponent(element, editable);
  else if (name === "Division")
    return new FormEmbedComponent(element, editable);
  else if (name === "Field")
    return new FormFieldComponent(element, editable);
  else
    return new FormBuilderComponent(element, editable);
}

export class FormBuilderComponent extends WebPageComponent {
  constructor(element, editable) {
    super(element);

    this.name = this.element.dataset.Name;
    this.container = this.element.dataset.Container == "true";
    this.canFocus = true;
    this.closable = true;
    this.editable = editable;

    this.determineElements();
  }

  createElement() {
    const instance = this.element.cloneNode();

    const close = document.createElement("div");
    close.classList.add("Close");
    close.tabIndex = 0;

    const up = document.createElement("div");
    up.classList.add("Up");
    up.tabIndex = 0;

    const down = document.createElement("div");
    down.classList.add("Down");
    down.tabIndex = 0;

    const icon = document.createElement("div");
    icon.classList.add("Icon");

    const title = document.createElement("div");
    title.classList.add("Title");

    const header = document.createElement("div");
    header.classList.add("Header");
    header.appendChild(icon);
    header.appendChild(title);

    if (this.captionElement !== null)
      title.innerHTML = this.captionElement.innerHTML;

    const form = document.createElement("div");
    form.classList.add("Properties");

    if (this.form !== null) {
      form.innerHTML = this.form.outerHTML;
      interactivityRegistration.attach(form);
    }

    if (this.canFocus)
      instance.tabIndex = 0;

    if (this.closable && this.editable) {
      instance.appendChild(close);
      instance.appendChild(up);
      instance.appendChild(down);
    }

    instance.appendChild(header);
    instance.appendChild(form);

    return instance;
  }

  createFormBuilderElement(editor, value) {
    const element = this.createElement();
    const instance = new DefaultFormBuilderElement(element, value, this);

    return instance;
  }

  createInstance(editor, value) {
    const instance = this.createFormBuilderElement(editor, value);

    if (instance.canFocus()) {
      instance.element.onclick = (event) => {
        editor.setTarget(instance);

        if (event != null)
          event.stopPropagation();
      };
    }
    else
      instance.element.onclick = (event) => {
        if (event != null)
          event.stopPropagation();
      };

    return instance;
  }

  createNewElement(name, container) {
    const element = document.createElement("div");
    element.classList.add(name);
    element.classList.add("Component");
    element.dataset.Name = name;
    element.dataset.Container = container;

    return element;
  }

  determineElements() {
    this.properties = new DomQuery(this.element).getChild(WithClass("Properties"));

    if (this.properties !== null)
      this.form = new DomQuery(this.properties).getChild(WithTagName("FORM"));
    else
      this.form = null;

    this.captionElement = new DomQuery(this.element).getChild(WithClass("Caption"));

    if (!this.editable && this.form !== null) {
      for (let index = 0; index < this.form.elements.length; index++) {
        this.form.elements[index].readOnly = true;
        this.form.elements[index].disabled = true;
      }

      const fields = new DomQuery(this.form).getDescendants(WithClass("edit"));

      for (const field of fields) {
        field.classList.remove("edit");
        field.classList.add("display");
      }
    }
  }
}

export class FormDocumentComponent extends FormBuilderComponent {
  constructor(element) {
    super(element);
  }

  createElement() {
    return this.element.cloneNode();
  }

  createFormBuilderElement(editor, value) {
    const element = this.createElement();
    const instance = new InlineFormBuilderElement(element, value, this);

    return instance;
  }
}

export class FormFieldComponent extends FormBuilderComponent {
  constructor(element, editable) {
    super(element, editable);

    this.caption = new FormDocumentComponent(this.createNewElement("Caption", false), false);
    this.caption.canFocus = false;
    this.caption.closable = false;

    this.labels = new FormDocumentComponent(this.createNewElement("Labels", false), false);
    this.labels.canFocus = false;
    this.labels.closable = false;
  }

  createFormBuilderElement(editor, value) {
    const element = new DefaultFormBuilderElement(this.createElement(), value, this);
    let caption = new DomQuery(value).getChild(WithTagName("Caption"));

    if (caption === null)
      caption = editor.xmlDocument.createElement("Caption");

    const caption_ = this.caption.createInstance(editor, caption);

    let labels = new DomQuery(value).getChild(WithTagName("Labels"));

    if (labels === null)
      labels = editor.xmlDocument.createElement("Labels");

    const labels_ = this.labels.createInstance(editor, labels);

    element.appendChild(caption_);
    element.appendChild(labels_);
    return element;
  }
}

export class FormEmbedComponent extends FormBuilderComponent {
  constructor(element, editable) {
    super(element, editable);

    this.canFocus = true;
  }

  createFormBuilderElement(editor, value) {
    const element = this.createElement();
    const instance = new EmbedFormBuilderElement(element, value, this);

    return instance;
  }
}

export class FormSectionComponent extends FormBuilderComponent {
  constructor(element, editable) {
    super(element, editable);

    this.title = new FormDocumentComponent(this.createNewElement("Title", false), false);
    this.title.canFocus = false;
    this.title.closable = false;

    this.contents = new FormBuilderComponent(this.createNewElement("Contents", true), false);
    this.contents.closable = false;
    this.contents.canFocus = false;

    this.canFocus = true;
  }

  createFormBuilderElement(editor, value) {
    const element = new DefaultFormBuilderElement(this.createElement(), value, this);

    let title = new DomQuery(value).getChild(WithTagName("Title"));
    let contents = new DomQuery(value).getChild(WithTagName("Contents"));

    if (title == undefined) {
      title = editor.xmlDocument.createElement("Title");
      contents = editor.xmlDocument.createElement("Contents");
    }

    const target = this.contents.createInstance(editor, contents);
    const title_ = this.title.createInstance(editor, title);

    element.appendChild(title_);
    element.appendChild(target);
    element.target = target;

    return element;
  }
}

function EditorType() {
  this.canFocus = true;
  this.container = true;
}

export class Editor extends WebPageComponent {
  constructor(element, object, name) {
    super(element);

    this.object = object;
    this.target = this;
    this.type = new EditorType();
    this.name = name;
    this.container = true;
    this.element.component = this;
  }

  _contains(object, element) {
    let result = false;

    for (const child of this.object.childNodes)
      result = result || (child === element);

    return result;
  }

  appendChild(element) {
    this.element.appendChild(element.element);

    if (!this._contains(this.object, element.object))
      this.object.appendChild(element.object);

    element.parent = this;
    element.readFromElement();
  }

  blur() {
    this.element.classList.remove("Target");
  }

  canFocus() {
    return true;
  }

  clear() {
    const components = new DomQuery(this.element).getChildren(WithClass("Component"));

    for (const component of components)
      this.element.removeChild(component);
  }

  focus() {
    this.element.focus();
    this.element.classList.add("Target");
  }

  writeToElement() {
    for (const child of this.element.childNodes) {
      if (child.component != undefined)
        child.component.writeToElement();
    }
  }
}

export class FormBuilderElement extends WebPageComponent {
  constructor(element, object, type) {
    super(element);

    this.object = object;
    this.type = type;
    this.container = this.type.container;
    this.target = this;
    this.element.component = this;

    this.determineElements();
    this.attachHandlers();
  }

  _contains(object, element) {
    let result = false;

    for (const child of this.object.childNodes)
      result = result || (child === element);

    return result;
  }

  after(element) {
    this.element.after(element.element);

    if (!this._contains(this.object.parentNode, element.object))
      this.object.after(element.object);

    element.parent = this.parent;
    element.readFromElement();
  }

  appendChild(element) {
    this.element.appendChild(element.element);

    if (!this._contains(this.object, element.object))
      this.object.appendChild(element.object);

    element.parent = this;
    element.readFromElement();
  }

  attachHandlers() {
    if (this.closeButton != undefined) {
      this.closeButton.onclick = (event) => { this.close(); event.stopPropagation(); };
      this.moveDownButton.onclick = (event) => { this.moveDown(); event.stopPropagation(); };
      this.moveUpButton.onclick = (event) => { this.moveUp(); event.stopPropagation(); };
    }
  }

  attributeToBoolean(value) {
    return value === "True";
  }

  before(element) {
    this.element.before(element.element);

    if (!this._contains(this.object.parentNode, element.object))
      this.object.before(element.object);

    element.parent = this.parent;
    element.readFromElement();
  }

  booleanToAttribute(value) {
    if (value)
      return "True";
    else
      return "False";
  }

  blur() {
    this.element.classList.remove("Target");
    this.writeToElement();
    this.updateHeader();
  }

  canFocus() {
    return this.type.canFocus;
  }

  close() {
    this.remove();
  }

  determineElements() {
    const query = new DomQuery(this.element);

    this.header = query.getChild(WithClass("Header"));

    if (this.header != undefined)
      this.title = this.header.childNodes[1];

    this.closeButton = query.getChild(WithClass("Close"));
    this.moveDownButton = query.getChild(WithClass("Down"));
    this.moveUpButton = query.getChild(WithClass("Up"));

    this.form = query.getChild(WithClass("Properties"));

    this.fields = new Array();

    if (this.form != undefined) {
      const formQuery = new DomQuery(this.form);

      Array.prototype.push.apply(this.fields, formQuery.getDescendants(WithTagName("INPUT")));
      Array.prototype.push.apply(this.fields, formQuery.getDescendants(WithTagName("TEXTAREA")));

      this.fields = this.fields.filter((field, index) => {
        const formId = this.form.childNodes[0].id;
        return (field.name !== "form") && (field.name.startsWith(formId) || field.name === "Type");
      });
    }

    for (const field of this.fields) {
      const fieldName = field.name.split("/").pop();

      if (fieldName === "FieldType")
        field.readOnly = true;
    }
  }

  focus() {
    this.element.focus();
    this.element.classList.add("Target");
  }

  getName() {
    const field = this.getNameField();

    if (field != null)
      return field.value;
    else
      return "";
  }

  getNameField() {
    let result = null;

    for (const field of this.fields) {
      const fieldName = field.name.split("/").pop();

      if (fieldName == "Name")
        result = field;
    }

    return result;
  }

  moveDown() {
    if (this.element.nextSibling !== null && this.element.nextSibling.component !== undefined) {
      const next = this.element.nextSibling.component;

      this.remove();
      next.after(this);
      this.element.onclick();
    }
  }

  moveUp() {
    if (this.element.previousSibling.component !== undefined) {
      const previous = this.element.previousSibling.component;

      this.remove();
      previous.before(this);
      this.element.onclick();
    }
  }

  readField(fieldName) {
    return this.object.getAttribute(fieldName);
  }

  remove() {
    this.parent.element.removeChild(this.element);
    this.parent.object.removeChild(this.object);

    if (this.parent.type.canFocus) {
      this.parent.element.focus();
      this.parent.element.onclick(null);
    }
    else {
      this.parent.parent.element.focus();
      this.parent.parent.element.onclick(null);
    }
  }

  updateHeader() {
    if (this.header != undefined) {
      const name = this.getName();

      if (name.length > 0)
        this.header.childNodes[1].innerHTML = name;
      else
        this.header.childNodes[1].innerHTML = this.title.innerHTML;
    }
  }

  writeField(fieldName, value) {
    this.object.setAttribute(fieldName, value);
  }
}

export class DefaultFormBuilderElement extends FormBuilderElement {
  constructor(element, object, type) {
    super(element, object, type);
  }

  readFromElement() {
    for (const field of this.fields) {
      const fieldName = field.name.split("/").pop();

      if (fieldName === "Optional")
        field.checked = this.attributeToBoolean(this.readField(fieldName));
      else {
        if (this.object.hasAttribute(fieldName)) {

          if (field.type === "checkbox")
            field.checked = this.attributeToBoolean(this.readField(fieldName));
          else
            field.value = this.readField(fieldName);
        }
      }

      if (field.onchange != undefined)
        field.onchange();
    }

    this.updateHeader();
  }

  writeToElement() {
    for (const field of this.fields) {
      const fieldName = field.name.split("/").pop();

      if (fieldName === "Optional")
        this.writeField(fieldName, this.booleanToAttribute(field.checked));
      else if (fieldName !== "Label" && fieldName !== "Contents") {
        if (field.type == "checkbox")
          this.writeField(fieldName, this.booleanToAttribute(field.checked));
        else if (field.value !== "")
          this.writeField(fieldName, field.value);
        else
          this.object.removeAttribute(fieldName);
      }
    }

    for (const child of this.element.childNodes) {
      if (child.component != undefined)
        child.component.writeToElement();
    }
  }
}

export class EmbedFormBuilderElement extends FormBuilderElement {
  constructor(element, object, type) {
    super(element, object, type);
  }

  readFromElement() {
    const field = this.fields[0];

    field.value = this.object.innerHTML;
  }

  writeToElement() {
    const field = this.fields[0];

    this.object.innerHTML = field.value;
  }
}

export class InlineFormBuilderElement extends FormBuilderElement {
  constructor(element, object, type, target) {
    super(element, object, type);
  }

  getCaptionElement() {
    const fields = new DomQuery(this.parent.form).getDescendants(WithTagName("TEXTAREA"));
    let result = null;

    for (const field of fields) {
      const fieldName = field.name.split("/").pop();

      if (fieldName === "Label")
        result = field;
    }

    return result;
  }

  getTypeElement() {
    let result = null;

    if (this.parent.form !== null)
      result = new DomQuery(this.parent.form).getDescendant(WithClass("TypeComponent"));

    return result;
  }

  readFromElement() {
    if (this.object.tagName === "Labels") {
      const typeField = this.getTypeElement();

      if (typeField.component !== undefined) {
        const labels = new Array();

        for (const child of this.object.childNodes) {
          labels.push({
            "Name": child.getAttribute("Name"),
            "Value": child.innerHTML
          });
        }

        typeField.component.setLabels(labels);
      }
    }
    else if (this.object.childNodes.length > 0)
      this.getCaptionElement().value = new XMLSerializer().serializeToString(this.object.childNodes[0]);
  }

  writeToElement() {
    if (this.object.tagName === "Labels") {
      const typeField = this.getTypeElement();

      if (typeField.component !== undefined) {
        const xmlDocument = document.implementation.createDocument("", "", null);
        const labelsElement = xmlDocument.createElement("Labels");
        const labels = typeField.component.getLabels();

        for (const label of labels) {
          const labelElement = xmlDocument.createElement("Label");
          labelElement.setAttribute("Name", label.Name);
          labelElement.innerHTML = label.Value;

          labelsElement.appendChild(labelElement);
        }

        this.object.innerHTML = labelsElement.innerHTML;
      }
    }
    else
      this.object.innerHTML = this.getCaptionElement().value;
  }
}

export function Progress(element) {
  this.build = function () {
    this.progress = document.createElement("div");
    this.progress.classList.add("Value");
    this.progress.style.width = "0%";

    this.element.appendChild(this.progress);
  }

  this.setProgress = function (value) {
    if (this.timeOutId !== 0)
      window.clearTimeout(this.timeOutId);

    this.progress.style.width = value + "%";
    this.progress.style.visibility = "visible";

    var object = this;

    if (value < 100 && value > 0) {
      var remainder = 100 - value;
      var timeOut = 1000 / remainder;

      this.timeOutId = window.setTimeout(function () { object.timeOutId = 0; object.setProgress(value + remainder / 25); }, timeOut);
    }
    else if (value === 100) {
      window.setTimeout(function () { object.setProgress(0); object.progress.style.visibility = "hidden"; }, 100);
    }
  }

  this.timeOutId = 0;
  this.element = element;
  this.build();
}

interactivityRegistration.register("FormBuilder", function (element) { return new FormBuilder(element); });
